import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Card,
  TextField,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Pagination,
  Tooltip,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Collapse,
} from "@mui/material";
import {
  FilterAlt as FilterAltIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DateRange as DateRangeIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import EditExpenseDialog from "./EditExpenseDialog";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const ExpensesList = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [pathname, setPathname] = useState("");
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // State management
  const [selectedExpenseType, setSelectedExpenseType] = useState("");
  const [selectedExpenseSource, setSelectedExpenseSource] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedExpense, setExpandedExpense] = useState(null);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [filePreviewOpen, setFilePreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  const tableRef = useRef(null);

  useEffect(() => {
    setPathname(location.pathname);
    if (location.pathname === "/reception/expenses") {
      setSelectedExpenseType("18");
    } else {
      setSelectedExpenseType("");
    }
    setPage(1);
  }, [location.pathname]);

  const periodOptions = [
    { label: t("finance.all", "All"), value: "all" },
    { label: t("finance.daily", "Daily"), value: "daily" },
    { label: t("finance.weekly", "Weekly"), value: "weekly" },
    { label: t("finance.monthly", "Monthly"), value: "monthly" },
    { label: t("finance.yearly", "Yearly"), value: "yearly" },
  ];

  // Data fetching
  const { data: expenseTypes } = useFetchObjects(
    ["expense-types"],
    "expense-types/",
    token
  );
  const { data: expenseSources } = useFetchObjects(
    ["expenseSources"],
    "expense-sources/",
    token
  );

  const buildQueryParams = () => {
    let params = new URLSearchParams();

    if (pathname === "/reception/expenses") {
      params.append("expenseType", "18");
    } else if (selectedExpenseType) {
      params.append("expenseType", selectedExpenseType);
    }

    if (selectedExpenseSource)
      params.append("expenseSource", selectedExpenseSource);
    if (periodFilter && periodFilter !== "all")
      params.append("period", periodFilter);
    if (searchTerm.trim()) params.append("search", searchTerm.trim());
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    params.append("page", page);
    params.append("page_size", rowsPerPage);

    return params.toString();
  };

  const { data, isLoading, isError, refetch } = useFetchObjects(
    [
      "expenses",
      page,
      rowsPerPage,
      selectedExpenseType,
      selectedExpenseSource,
      periodFilter,
      searchTerm,
      dateFrom,
      dateTo,
    ],
    `expenses/?${buildQueryParams()}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("expenses", token);

  useEffect(() => {
    if (data?.results) {
      setFilteredExpenses(data.results);
      setTotalExpenses(data.count);
    }
  }, [data]);

  // Helper functions
  const toggleExpenseExpansion = (expenseId) => {
    setExpandedExpense(expandedExpense === expenseId ? null : expenseId);
  };

  const getFileIcon = (file) => {
    if (!file || !file.file_type) return <InsertDriveFileIcon />;
    const type = file.file_type.split("/")[0];
    const extension = file.file_name?.split(".").pop()?.toLowerCase();

    if (type === "image") return <ImageIcon />;
    if (type === "application") {
      if (extension === "pdf") return <PdfIcon />;
      return <DescriptionIcon />;
    }
    if (type === "video") return <VideoIcon />;
    if (type === "audio") return <AudioIcon />;
    return <InsertDriveFileIcon />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(parseISO(dateString), "MMM dd, yyyy");
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    try {
      const [hours, minutes] = timeString.split(":");
      let hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      hour = hour % 12 || 12;
      return `${hour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const handleFilePreview = (file) => {
    if (file) {
      setSelectedFile(file);
      setFilePreviewOpen(true);
    }
  };

  const closeFilePreview = () => {
    setFilePreviewOpen(false);
    setSelectedFile(null);
  };

  const handlePageChange = (event, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const openEditDialog = (expense) => {
    setSelectedExpense(expense);
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedExpense(null);
    refetch();
  };

  const totalExpenseAmount = filteredExpenses.reduce(
    (acc, expense) => acc + (expense.amount || 0),
    0
  );

  const handleDownloadPDF = async () => {
    if (!tableRef.current) return;
    const canvas = await html2canvas(tableRef.current, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("landscape", "pt", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const imgWidth = pdfWidth - 40;
    const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

    pdf.addImage(imgData, "PNG", 20, 20, imgWidth, imgHeight);
    pdf.save("expenses_list.pdf");
  };

  const clearDateFilters = () => {
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  return (
    <Grid container mb={4} mt={2} pb={5} justifyContent="center">
      <Grid item xs={12} md={11} lg={10}>
        {/* Filter Section */}
        <Card
          sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: theme.shadows[3] }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: isSmallScreen ? "column" : "row",
              gap: 2,
              mb: 2,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              sx={{ color: theme.palette.primary.main }}
            >
              <FilterAltIcon sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight={600} color="text.primary">
                {t("finance.filterExpenses")}
              </Typography>
            </Box>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  pathname === "/finance/expenses"
                    ? navigate("/finance/expenses/add")
                    : navigate("/reception/add-expense");
                }}
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                }}
              >
                {t("finance.addNew")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={handleDownloadPDF}
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                }}
              >
                {t("finance.downloadPdf", "Download as PDF")}
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {/* Expense Type */}
            <Grid item xs={12} md={3}>
              <FormControl
                fullWidth
                size="small"
                disabled={pathname === "/reception/expenses"}
              >
                <InputLabel>{t("finance.expenseType")}</InputLabel>
                <Select
                  value={selectedExpenseType}
                  onChange={(e) => {
                    setSelectedExpenseType(e.target.value);
                    setPage(1);
                  }}
                  label={t("finance.expenseType")}
                  sx={{ borderRadius: 1 }}
                >
                  <MenuItem value="">{t("finance.all")}</MenuItem>
                  {expenseTypes?.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Expense Source */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("finance.expenseSource")}</InputLabel>
                <Select
                  value={selectedExpenseSource}
                  onChange={(e) => {
                    setSelectedExpenseSource(e.target.value);
                    setPage(1);
                  }}
                  label={t("finance.expenseSource")}
                  sx={{ borderRadius: 1 }}
                >
                  <MenuItem value="">{t("finance.all")}</MenuItem>
                  {expenseSources?.map((source) => (
                    <MenuItem key={source.id} value={source.id}>
                      {source.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Period Filter */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("finance.period", "Period")}</InputLabel>
                <Select
                  value={periodFilter}
                  onChange={(e) => {
                    setPeriodFilter(e.target.value);
                    setPage(1);
                  }}
                  label={t("finance.period", "Period")}
                  sx={{ borderRadius: 1 }}
                >
                  {periodOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Search by Company or Invoice */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder={t(
                  "finance.searchCompanyInvoice",
                  "Search by Company or Invoice"
                )}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                sx={{ borderRadius: 1 }}
              />
            </Grid>

            {/* Date From Filter */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label={t("finance.dateFrom", "Date From")}
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <DateRangeIcon color="action" sx={{ mr: 1 }} />
                  ),
                }}
                sx={{ borderRadius: 1 }}
              />
            </Grid>

            {/* Date To Filter */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label={t("finance.dateTo", "Date To")}
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <DateRangeIcon color="action" sx={{ mr: 1 }} />
                  ),
                }}
                sx={{ borderRadius: 1 }}
              />
            </Grid>

            {/* Clear Date Filters */}
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                onClick={clearDateFilters}
                disabled={!dateFrom && !dateTo}
                fullWidth
                sx={{ height: "40px", borderRadius: 1 }}
              >
                {t("finance.clearDates", "Clear Dates")}
              </Button>
            </Grid>
          </Grid>
        </Card>

        {/* Expense Table */}
        {isLoading ? (
          <Box textAlign="center" p={4}>
            <CircularProgress />
            <Typography mt={2}>{t("finance.loading")}</Typography>
          </Box>
        ) : isError ? (
          <Box textAlign="center" color="error.main" p={4}>
            <Typography>{t("finance.errorFetchingData")}</Typography>
          </Box>
        ) : (
          <TableContainer
            ref={tableRef}
            component={Paper}
            sx={{ borderRadius: 2, boxShadow: theme.shadows[3] }}
          >
            <Table>
              <TableHead sx={{ bgcolor: theme.palette.grey[100] }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    {t("finance.expenseType")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    {t("finance.expenseSource")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    {t("finance.company", "Company")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    {t("finance.invoice", "Invoice Number")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    {t("finance.amount")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    {t("finance.description")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    {t("finance.files")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    {t("finance.date")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    {t("finance.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExpenses.length > 0 ? (
                  filteredExpenses.map((expense) => (
                    <React.Fragment key={expense.id}>
                      <TableRow
                        hover
                        sx={{
                          "&:hover": { bgcolor: theme.palette.action.hover },
                        }}
                      >
                        <TableCell>{expense.id}</TableCell>
                        <TableCell align="center">
                          {expense.expenseType_display}
                        </TableCell>
                        <TableCell align="center">
                          {expense.expenseSource_display}
                        </TableCell>
                        <TableCell align="center">
                          {expense.company_name || "-"}
                        </TableCell>
                        <TableCell align="center">
                          {expense.invoice_number || "-"}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.success.main,
                          }}
                        >
                          {expense.amount?.toLocaleString()}
                        </TableCell>
                        <TableCell align="center" sx={{ maxWidth: 200 }}>
                          {expense.description ? (
                            <Tooltip title={expense.description}>
                              <Typography
                                variant="body2"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {expense.description}
                              </Typography>
                            </Tooltip>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ width: 150 }}>
                          {expense.files?.length > 0 ? (
                            <Badge
                              badgeContent={expense.files.length}
                              color="primary"
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DescriptionIcon />}
                                onClick={() =>
                                  toggleExpenseExpansion(expense.id)
                                }
                                endIcon={
                                  expandedExpense === expense.id ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )
                                }
                                sx={{ borderRadius: 1 }}
                              >
                                {t("finance.viewFiles")}
                              </Button>
                            </Badge>
                          ) : (
                            <Typography
                              variant="body2"
                              color={theme.palette.text.disabled}
                            >
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Typography variant="body2">
                              {formatDate(expense.date)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatTime(expense.time)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <Tooltip title={t("finance.edit")}>
                              <IconButton
                                onClick={() => openEditDialog(expense)}
                                size="small"
                                sx={{ color: theme.palette.primary.main }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("finance.delete")}>
                              <IconButton
                                onClick={() => handleDelete(expense.id)}
                                size="small"
                                sx={{ color: theme.palette.error.main }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {expense.files?.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
                            <Collapse
                              in={expandedExpense === expense.id}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  p: 3,
                                  bgcolor: theme.palette.background.default,
                                  borderTop: `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Typography variant="subtitle2" gutterBottom>
                                  {t("finance.attachedFiles")} (
                                  {expense.files.length})
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 2,
                                  }}
                                >
                                  {expense.files.map(
                                    (file, index) =>
                                      file && (
                                        <Card
                                          key={index}
                                          sx={{
                                            p: 1,
                                            width: 120,
                                            cursor: "pointer",
                                            borderRadius: 1,
                                            boxShadow: 1,
                                            "&:hover": { boxShadow: 4 },
                                          }}
                                          onClick={() =>
                                            handleFilePreview(file)
                                          }
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              alignItems: "center",
                                              textAlign: "center",
                                            }}
                                          >
                                            <Avatar
                                              sx={{
                                                width: 48,
                                                height: 48,
                                                bgcolor:
                                                  theme.palette.grey[100],
                                                color:
                                                  theme.palette.text.secondary,
                                                mb: 1,
                                              }}
                                            >
                                              {getFileIcon(file)}
                                            </Avatar>
                                            <Typography
                                              variant="caption"
                                              noWrap
                                              sx={{
                                                width: "100%",
                                                fontWeight: 500,
                                              }}
                                            >
                                              {file.file_name ||
                                                file.file?.split("/").pop() ||
                                                "File"}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              {formatFileSize(file.file_size)}
                                            </Typography>
                                          </Box>
                                        </Card>
                                      )
                                  )}
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={{ py: 4, color: theme.palette.text.secondary }}
                    >
                      <Typography>{t("finance.noExpensesFound")}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {/* Total row */}
                <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                  <TableCell colSpan={5} />
                  <TableCell
                    align="right"
                    colSpan={2}
                    sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                  >
                    {t("finance.total")}: {totalExpenseAmount?.toLocaleString()}
                  </TableCell>
                  <TableCell colSpan={3} />
                </TableRow>
              </TableBody>
            </Table>

            {/* Pagination */}
            <Box
              display="flex"
              justifyContent="space-between"
              p={2}
              sx={{ bgcolor: theme.palette.background.paper }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color={theme.palette.text.primary}>
                  {t("finance.rowsPerPage")}:
                </Typography>
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  size="small"
                  sx={{ borderRadius: 1 }}
                >
                  {[10, 25, 50, 100].map((rows) => (
                    <MenuItem key={rows} value={rows}>
                      {rows}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Pagination
                count={Math.ceil(totalExpenses / rowsPerPage)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </TableContainer>
        )}

        {/* File Preview Dialog */}
        {selectedFile && (
          <Dialog
            open={filePreviewOpen}
            onClose={closeFilePreview}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle
              sx={{
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              }}
            >
              {selectedFile.file_name ||
                selectedFile.file?.split("/").pop() ||
                "File Preview"}
            </DialogTitle>
            <DialogContent>
              {selectedFile.file_type?.includes("image") ? (
                <Box
                  component="img"
                  src={selectedFile.file}
                  alt={selectedFile.file_name || "Preview"}
                  sx={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "50vh",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: theme.palette.grey[100],
                      color: theme.palette.text.secondary,
                      mb: 2,
                    }}
                  >
                    {getFileIcon(selectedFile)}
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    {selectedFile.file_name ||
                      selectedFile.file?.split("/").pop() ||
                      "File"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {formatFileSize(selectedFile.file_size)}
                  </Typography>
                  <Button
                    variant="contained"
                    component="a"
                    href={selectedFile.file}
                    target="_blank"
                    download
                    startIcon={<FileDownloadIcon />}
                    sx={{ mt: 2 }}
                  >
                    {t("finance.download")}
                  </Button>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeFilePreview} sx={{ textTransform: "none" }}>
                {t("finance.close")}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        <ConfirmDialog />
        {selectedExpense && (
          <EditExpenseDialog
            open={editDialogOpen}
            onClose={closeEditDialog}
            expense={selectedExpense}
            token={token}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default ExpensesList;
